<script setup lang="ts">
import { useId } from 'vue';

const props = defineProps<{
	id?: string;
	name?: string;
	label?: string;
	description?: string;
	modelValue?: string | boolean | number;
	checked?: boolean;
	disabled?: boolean;
	ariaLabel?: string;
}>();

const model = defineModel<string | boolean | number>();

const id = props.id ?? useId();
</script>

<template>
	<div class="form-switch">
		<input
			type="checkbox"
			:name="props.name"
			:disabled="props.disabled"
			:checked="props.checked"
			:aria-label="props.ariaLabel"
			:id="id"
			@change="model = ($event.target as HTMLInputElement).checked"
		/>
		<label :for="id">
			<span class="form-switch__content" v-if="props.label">
				<span class="form-switch__label">{{ props.label }}</span>
				<span class="form-switch__description" v-if="props.description">{{ props.description }}</span>
			</span>
		</label>
	</div>
</template>

<style scoped lang="scss">
@layer hm-components
{
	// Switch
	.form-switch
	{
		@include flex;
		line-height:1.5;
		position:relative;

		input
		{
			position:absolute;
			inset:0 auto auto 0;

			~ label
			{
				@include flex(null, center, null, true);
				min-height:2.4rem;
				color:var(--c-body);
				cursor:pointer;
				user-select:none;
				padding-inline-start:4.8rem;
				position:relative;
				transition:var(--transition-default);

				&::before
				{
					content:'';
					@include size(48, 24);
					border:1px solid var(--basic-600);
					border-radius:var(--radius-full);
					background-color:var(--bg-primary);
					position:absolute;
					inset:0 auto auto 0;
					transition:var(--transition-default);
				}
				&::after
				{
					content:'';
					@include size(16);
					border:1px solid transparent;
					border-radius:var(--radius-full);
					background-color:var(--basic-600);
					position:absolute;
					inset:var(--spacing-4xs) auto auto var(--spacing-4xs);
					transition:var(--transition-default);
				}
			}

			// Checked
			&:checked
			{
				~ label
				{
					&::before
					{
						border-color:var(--accent-primary);
						background-color:var(--accent-primary);
					}
					&::after
					{
						background-color:var(--bg-primary);
						translate:calc(100% + 0.8rem);
					}
				}
			}

			// Disabled
			&:disabled
			{
				~ label
				{
					cursor:not-allowed;

					&::before
					{
						border-color:var(--basic-500);
						background-color:var(--border-secondary);
					}
					&::after
					{
						background-color:var(--basic-500);
					}
					.form-switch__text
					{
						color:var(--c-subtitle);
					}
				}
			}

			// Checked & Disabled
			&:checked:disabled
			{
				~ label
				{
					&::before
					{
						border-color:var(--primary-950);
						background-color:var(--primary-950);
					}
					&::after
					{
						background-color:var(--border-primary);
					}
				}
			}

			&:focus-visible ~ label
			{
				outline:auto;
				outline-color:-webkit-focus-ring-color;
			}
		}

		&__content
		{
			@include flex(null, null, var(--spacing-4xs));
			flex-direction:column;
			padding-inline-start:var(--spacing-2xs);
		}
		&__label
		{
			color:var(--c-body);
			@include text-body(sm);
		}
		&__description
		{
			color:var(--c-subtitle);
			@include text-caption;
		}
	}
}
</style>
